import { v4 as uuidv4 } from 'uuid';

import { EUserRole } from '@/enum';
import { TAssetValue } from '@/types';

export const isValidUserRole = (value: unknown): value is EUserRole =>
  value === EUserRole.SCHOOL ||
  value === EUserRole.TEACHER ||
  value === EUserRole.PARENT;

/** Flatten nested JSON messages for react-intl */
export const flattenMessages = (
  obj: Record<string, unknown>,
  prefix = ''
): Record<string, string> => {
  return Object.keys(obj).reduce(
    (acc, key) => {
      const value = obj[key];
      const newKey = prefix ? `${prefix}.${key}` : key;

      if (value && typeof value === 'object' && !Array.isArray(value)) {
        Object.assign(
          acc,
          flattenMessages(value as Record<string, unknown>, newKey)
        );
      } else {
        acc[newKey] = String(value);
      }
      return acc;
    },
    {} as Record<string, string>
  );
};

function setCookie(name: string, value: string, days = 365) {
  const maxAge = days * 24 * 60 * 60; // seconds
  document.cookie = `${name}=${value}; path=/; max-age=${maxAge}; SameSite=Lax`;
}

function getCookie(name: string) {
  const regex = new RegExp(`(^|;)\\s*${name}\\s*=\\s*([^;]+)`);
  const cookieValue = regex.exec(document.cookie);
  return cookieValue ? cookieValue.pop() : '';
}

export function generateOrGetUserId() {
  let userId = getCookie('device_id');
  if (!userId) {
    userId = uuidv4();
    setCookie('device_id', userId);
  }
  return userId;
}

export const pickBy = (
  obj: Record<string, unknown>,
  predicate: (value: unknown, key: string) => boolean
) => {
  return Object.fromEntries(
    Object.entries(obj).filter(([key, value]) => predicate(value, key))
  );
};

export const customPredicate = (value: unknown) =>
  value !== null && value !== undefined && value !== '';

export const formatQuery = (query: Record<string, unknown>): string => {
  if (!query || typeof query !== 'object') {
    return '';
  }

  const filteredQuery = pickBy(query, customPredicate);

  const buildQueryParams = (
    obj: Record<string, unknown>,
    parentKey = ''
  ): string[] => {
    return Object.entries(obj).flatMap(([key, value]) => {
      const fullKey = parentKey ? `${parentKey}[${key}]` : key;

      if (Array.isArray(value)) {
        return value.map(
          (item, index) =>
            `${fullKey}[${index}]=${encodeURIComponent(String(item))}`
        );
      } else if (value && typeof value === 'object') {
        return buildQueryParams(value as Record<string, unknown>, fullKey);
      } else {
        return [`${fullKey}=${encodeURIComponent(String(value))}`];
      }
    });
  };

  return buildQueryParams(filteredQuery).join('&');
};

export const formatLastActive = (
  timestamp?: number,
  formatText?: (key: string, values?: Record<string, string | number>) => string
) => {
  if (!timestamp) return '-';

  const now = Math.floor(Date.now() / 1000);
  const diff = now - timestamp;

  const formatUnit = (key: string, value: number, fallback: string) =>
    formatText ? formatText(key, { value }) : fallback;

  if (diff < 60)
    return formatText ? formatText('common.time.just-now') : 'Just now';

  const minutes = Math.floor(diff / 60);
  if (minutes < 60)
    return formatUnit('common.time.minutes-ago', minutes, `${minutes}m ago`);

  const hours = Math.floor(minutes / 60);
  if (hours < 24)
    return formatUnit('common.time.hours-ago', hours, `${hours}h ago`);

  const days = Math.floor(hours / 24);
  if (days < 30)
    return formatUnit('common.time.days-ago', days, `${days}d ago`);

  const months = Math.floor(days / 30);
  if (months < 12)
    return formatUnit('common.time.months-ago', months, `${months}mo ago`);

  const years = Math.floor(months / 12);
  return formatUnit('common.time.years-ago', years, `${years}y ago`);
};

export const getCurrentTimeInUnix = () => {
  const time = Math.floor(Date.now() / 1000);
  return time;
};

export const getStartOfMonthUnix = () => {
  const date = new Date();
  date.setDate(1);
  date.setHours(0, 0, 0, 0);
  return Math.floor(date.getTime() / 1000);
};

//unix to long date format
export const formatUnixToLongDate = (unixTimestamp: number): string => {
  const date = new Date(unixTimestamp * 1000);

  return date.toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
};

export const convertToTimestamp = (date: string | null) =>
  date ? Math.floor(new Date(date).getTime() / 1000) : null;

export const formatDate = (date: number | string) => {
  if (!date) return '-';
  const d = new Date(typeof date === 'number' ? date * 1000 : date);
  return d.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
};

export const sanitizeFileName = (name: string): string =>
  (name ?? '')
    .replace(/[/\\:*?"<>|]/g, '_')
    .replace(/\s+/g, '_')
    .trim() || 'download';

export const downloadFile = async (blob: Blob, fileName: string) => {
  const safeFileName = sanitizeFileName(fileName);
  let finalBlob = blob;
  if (safeFileName.endsWith('.csv')) {
    const text = await blob.text();
    finalBlob = new Blob(['\uFEFF' + text], {
      type: 'text/csv;charset=utf-8;',
    });
  }
  const url = window.URL.createObjectURL(finalBlob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', safeFileName);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

export const downloadFileFromUrl = (url: string, fileName: string) => {
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', sanitizeFileName(fileName));
  link.rel = 'noopener noreferrer';
  document.body.appendChild(link);
  link.click();
  link.remove();
};

export const formatSecondsToDuration = (seconds?: number | string): string => {
  const total = Number(seconds);

  if (!Number.isFinite(total) || total <= 0) {
    return '0m';
  }

  const days = Math.floor(total / 86400);

  const remainingAfterDays = total % 86400;

  const hours = Math.floor(remainingAfterDays / 3600);

  const remainingAfterHours = remainingAfterDays % 3600;

  const minutes = Math.round(remainingAfterHours / 60);

  // Handle rounding overflow
  const normalizedHours = minutes === 60 ? hours + 1 : hours;
  const normalizedMinutes = minutes === 60 ? 0 : minutes;

  if (days > 0) {
    return normalizedHours > 0 ? `${days}d ${normalizedHours}h` : `${days}d`;
  }

  if (normalizedHours > 0) {
    return normalizedMinutes > 0
      ? `${normalizedHours}h ${normalizedMinutes}m`
      : `${normalizedHours}h`;
  }

  return `${normalizedMinutes}m`;
};

export const getAssetUrl = (asset?: TAssetValue): string => {
  if (!asset) return '';
  if (typeof asset === 'string') return asset;
  return `${asset.url || asset.base_url || ''}${asset.root || ''}${asset.folder || ''}${asset.name || ''}`;
};
